import { type QueryClient, useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef } from 'react'

import { loungeKeys } from '@/features/lounges/api'
import { nookKeys } from '@/features/nooks/api'
import { graphql } from '@/gql'
import type { MyLoungesQuery, NooksQuery } from '@/gql/graphql'
import { subscribe } from '@/lib/wsClient'

/**
 * Badges: the one subscription that keeps them live, and every write that moves
 * a number.
 *
 * One socket subscription for the whole app, mounted once at the shell,
 * covering every lounge you are in — including the ones you are not looking at.
 * That is why it is addressed by *recipient* rather than by place: a per-lounge
 * stream can only tell you about lounges you already subscribed to, which by
 * definition excludes the one you were just invited to.
 *
 * Nothing else in the app increments an unread count.
 */

const ActivityStreamSubscription = graphql(`
  subscription ActivityStream {
    activityStream {
      kind
      loungeId
      nookId
      mentionsYou
    }
  }
`)

/**
 * The server's cap, mirrored.
 *
 * `server/src/service/read_state.rs` clamps both the per-nook count and the
 * per-lounge total at this number, in SQL. Incrementing past it here would show
 * a badge that no refetch could ever reproduce. It cannot travel in the schema,
 * so a mirror with a comment naming its source is the honest structure.
 */
const UNREAD_CAP = 99

const bump = (count: number, by: number) => Math.min(count + by, UNREAD_CAP)

interface ActivityEvent {
  kind: 'MESSAGE_SENT'
  loungeId: string
  nookId?: string | null
  mentionsYou: boolean
}

/**
 * Fold one activity event into the caches that draw badges.
 *
 * Exported and free of hooks so it can be reasoned about — and tested, the day
 * this project has a runner — without a socket.
 *
 * Note what is *not* here: any check on who wrote the message. The server never
 * publishes this event to the author, because the unread SQL already excludes
 * your own words. Making that a publish-time rule rather than a client-side
 * filter is what keeps the live count and the refetched count agreeing.
 */
export function applyActivity(
  client: QueryClient,
  event: ActivityEvent,
  openNookId: string | undefined,
) {
  // Future kinds land here and are ignored, which is the point of one generic
  // event: an older SPA against a newer server misses a badge rather than
  // throwing.
  if (event.kind !== 'MESSAGE_SENT' || !event.nookId) return

  // The room on screen belongs to the timeline. Its own subscription puts the
  // message in the list and `markRead` clears the count a beat later, so
  // counting here as well would flash a badge onto the room you are reading.
  if (event.nookId === openNookId) return

  // The envelope, never the bare array — the convention every cache write in
  // this app follows.
  client.setQueryData<NooksQuery>(nookKeys.list(event.loungeId), (current) => {
    if (!current) return current
    // A lounge whose list was never fetched, or a room created since it was:
    // say nothing rather than invent a row. Opening the lounge refetches.
    if (!current.nooks.some((nook) => nook.id === event.nookId)) return current

    return {
      nooks: current.nooks.map((nook) =>
        nook.id === event.nookId
          ? {
              ...nook,
              unreadCount: bump(nook.unreadCount, 1),
              mentionCount: event.mentionsYou ? bump(nook.mentionCount, 1) : nook.mentionCount,
            }
          : nook,
      ),
    }
  })

  // The rail, separately and always — this is what makes a lounge you have not
  // opened this session light up at all.
  client.setQueryData<MyLoungesQuery>(loungeKeys.mine(), (current) => {
    if (!current) return current

    return {
      myLounges: current.myLounges.map((lounge) =>
        lounge.id === event.loungeId
          ? {
              ...lounge,
              unreadCount: bump(lounge.unreadCount, 1),
              mentionCount: event.mentionsYou
                ? bump(lounge.mentionCount, 1)
                : lounge.mentionCount,
            }
          : lounge,
      ),
    }
  })
}

/**
 * Clear a nook's badge, and take the same amount off its lounge's.
 *
 * A lounge total is **not** the sum of its rooms — both are capped at 99
 * independently, in SQL — so this subtracts what this room was carrying rather
 * than recomputing from the list, and clamps at zero. When the room was sitting
 * at the cap the subtraction is a lower bound; the invalidate that follows the
 * mutation is what makes it exact.
 */
export function clearUnread(client: QueryClient, nookId: string) {
  for (const [key, data] of client.getQueriesData<NooksQuery>({ queryKey: nookKeys.lists() })) {
    const nook = data?.nooks.find((candidate) => candidate.id === nookId)
    if (!data || !nook) continue
    if (nook.unreadCount === 0 && nook.mentionCount === 0) continue

    client.setQueryData<NooksQuery>(key, {
      nooks: data.nooks.map((candidate) =>
        candidate.id === nookId
          ? { ...candidate, unreadCount: 0, mentionCount: 0 }
          : candidate,
      ),
    })

    client.setQueryData<MyLoungesQuery>(loungeKeys.mine(), (current) =>
      current
        ? {
            myLounges: current.myLounges.map((lounge) =>
              lounge.id === nook.loungeId
                ? {
                    ...lounge,
                    unreadCount: Math.max(0, lounge.unreadCount - nook.unreadCount),
                    mentionCount: Math.max(0, lounge.mentionCount - nook.mentionCount),
                  }
                : lounge,
            ),
          }
        : current,
    )
  }
}

/**
 * Keep every badge in the app live, for as long as the shell is mounted.
 *
 * `openNookId` comes from the router, in the component that mounts this — the
 * module stays router-free, like every other feature's `api.ts`.
 */
export function useActivityStream(openNookId: string | undefined) {
  const client = useQueryClient()

  // A ref, and deliberately NOT an effect dependency. The open room changes on
  // every navigation, and listing it below would tear the subscription down and
  // rebuild it on each one — which fires `onStart`, turning every click on a
  // nook into a full refetch of every badge in the app, and opening a window
  // between the two subscriptions where events are simply lost.
  const openNook = useRef(openNookId)
  openNook.current = openNookId

  useEffect(() => {
    return subscribe(
      ActivityStreamSubscription,
      {},
      {
        onNext: ({ activityStream: event }) => {
          applyActivity(client, event, openNook.current)
        },
        // Any gap — a dropped socket, or a stream the server ended — is
        // repaired by asking again. Both keys, because both hold counts this
        // stream is the only live source for.
        onStart: () => {
          void client.invalidateQueries({ queryKey: nookKeys.lists() })
          void client.invalidateQueries({ queryKey: loungeKeys.mine() })
        },
      },
    )
    // `openNookId` is intentionally absent — see the ref above.
  }, [client])
}
